import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { toneSurfaces } from '#app/components/ui/alert.tsx'
import { cn } from '#app/utils/misc.tsx'

/**
 * Badge: a pill label, one size everywhere. `variant` picks a brand or neutral
 * look. `soft` is the tag and category chip: brand text on `--brand-soft` at
 * 85%, because the full tint misses 4.5:1 in light. Hover draws a brand border,
 * since any stronger tint drops the text below AA. On a `<Link>`, use
 * `badgeVariants({ variant: 'soft' })`. `tone` marks a status on Alert's
 * surfaces (`toneSurfaces`) and, when set, replaces the variant's colours:
 *   info    → `--info-surface` / `--info-border`, text in `--info-text`,
 *   success → `--success-surface` / `--success-border`, text in
 *             `--success-text`,
 *   warning → faint destructive ramp with the body text,
 *   error   → stronger destructive ramp with text in `text-error-text`,
 *   neutral → `--muted` with `--muted-foreground`.
 * Every tone's text holds 4.5:1 on its surface in both themes. Map a domain
 * status to its tone through `#app/utils/status-tone.ts`.
 */
const badgeVariants = cva(
	'text-body-2xs inline-flex h-6 items-center rounded-full border px-2 font-semibold',
	{
		variants: {
			variant: {
				default: 'bg-gradient-brand text-brand-foreground border-transparent',
				secondary: 'border-transparent bg-secondary text-secondary-foreground',
				destructive:
					'border-transparent bg-destructive text-destructive-foreground',
				outline: 'border-border text-foreground',
				soft: 'bg-brand-soft/85 text-brand hover:border-brand border-transparent',
			},
			tone: {
				info: [toneSurfaces.info, 'text-info-text'],
				success: [toneSurfaces.success, 'text-success-text'],
				warning: [toneSurfaces.warning, 'text-foreground'],
				error: [toneSurfaces.error, 'text-error-text'],
				neutral: 'border-border bg-muted text-muted-foreground',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

export type BadgeVariant = VariantProps<typeof badgeVariants>
export type BadgeTone = NonNullable<BadgeVariant['tone']>

const Badge = ({
	className,
	variant,
	tone,
	...props
}: React.ComponentProps<'span'> & BadgeVariant) => (
	<span
		data-slot="badge"
		data-tone={tone ?? undefined}
		className={cn(
			// A null variant skips the default too, so a tone paints alone.
			badgeVariants({ variant: tone ? null : variant, tone, className }),
		)}
		{...props}
	/>
)

export { Badge, badgeVariants }
